import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { forwardRef } from 'react';
import { FOCUS_RING } from '@/Components/Marketing/focusRing';
import SmartLink, { hrefKind } from '@/Components/Marketing/SmartLink';
import { useTone } from '@/Components/Marketing/Tone';
import { cn } from '@/lib/utils';

// The product app's button (Components/ui/button.jsx) at marketing scale: a
// rounded-lg box with a 1px border, the app's inset top highlight and a small
// grey-tinted drop shadow; never a pill. Orange only as the `accent` variant.
const BASE = cn(
    'group inline-flex w-fit items-center justify-center whitespace-nowrap rounded-lg border font-medium',
    'transition duration-300 ease-mk active:scale-[0.98] motion-reduce:active:scale-100',
    FOCUS_RING,
    'disabled:pointer-events-none disabled:opacity-50',
);

const SURFACE = {
    primary: {
        light: cn(
            'border-mk-ink bg-mk-ink text-white hover:bg-mk-ink/90',
            'shadow-[inset_0_1px_theme(colors.white/15%),0_1px_2px_theme(colors.mk.ink/15%)]',
        ),
        dark: cn(
            'border-white bg-white text-mk-ink hover:bg-mk-sunken',
            'shadow-[inset_0_-1px_theme(colors.mk.ink/10%),0_1px_2px_theme(colors.mk.night/40%)]',
        ),
    },
    secondary: {
        light: cn(
            'border-mk-ink/10 bg-mk-surface text-mk-ink hover:bg-mk-sunken',
            'shadow-[0_1px_2px_theme(colors.mk.ink/6%)]',
        ),
        dark: 'border-white/15 text-mk-night-text hover:bg-white/5',
    },
    // The one orange button (the Platform page's closing "Book a demo", 2026-10-04):
    // white text (2026-10-05) on orange-ink, 5.2:1, where on the brand orange it
    // would read 3.2:1; its hover lightens no further than 4.57:1 (/92 over
    // white; /90 would drop to 4.42:1). On a dark surface it is the primary
    // (no orange fill on night).
    accent: {
        light: cn(
            'border-mk-orange-ink bg-mk-orange-ink text-white hover:bg-mk-orange-ink/[0.92]',
            'shadow-[inset_0_1px_theme(colors.white/20%),0_1px_2px_theme(colors.mk.ink/15%)]',
        ),
        dark: null,
    },
};

// A trailing icon takes a little of the right padding back, so the label and
// arrow together sit optically centred.
const SIZE = {
    md: { withIcon: 'h-10 gap-2 pl-4 pr-3.5 text-sm', bare: 'h-10 px-4 text-sm' },
    lg: { withIcon: 'h-11 gap-2 pl-5 pr-4 text-[15px]', bare: 'h-11 px-5 text-[15px]' },
};

/**
 * A light-surface Button's look without its icon, for an element Button does
 * not render: a plain same-tab <a> to a page outside Inertia (the Filament
 * admin panel), which SmartLink would visit as an Inertia page.
 */
export function buttonClass({ variant = 'primary', size = 'md' } = {}) {
    return cn(BASE, (SURFACE[variant] ?? SURFACE.primary).light, (SIZE[size] ?? SIZE.md).bare);
}

const ICON = cn(
    'size-4 shrink-0 transition-transform duration-300 ease-mk',
    // Only where a pointer can hover (a tap leaves no arrow nudged), and only with motion.
    'motion-safe:fine:group-hover:translate-x-0.5',
);

/**
 * The marketing CTA, styled like the product app's button. With `href` it is a
 * link (an Inertia <Link> inside the app, a new-tab <a> for other origins, see
 * SmartLink); without one it is a <button>. A small arrow trails the label
 * only on a primary or accent link, where it says where the link goes
 * (audits 26 and 47): ArrowUpRight when it leaves the site, ArrowRight to
 * another page of it. A submit or any other <button>, a link within the page
 * and a secondary (hairline) button carry none. `icon` sets another (a
 * download, a retry); `icon={null}` drops it. `variant="accent"` is the
 * orange one, for a page's single main call to action, on a light surface
 * only: on a dark one it draws as the primary.
 */
const Button = forwardRef(function Button(
    {
        href,
        variant = 'primary',
        tone,
        size = 'md',
        icon,
        external,
        type = 'button',
        className,
        children,
        ...props
    },
    ref,
) {
    const resolved = useTone(tone);
    const kind = SURFACE[variant] ? variant : 'primary';
    const dimensions = SIZE[size] ?? SIZE.md;
    const goes = href == null ? null : hrefKind(href, external);
    const arrow = kind === 'secondary' ? null : { external: ArrowUpRight, internal: ArrowRight }[goes] ?? null;
    const Icon = icon === undefined ? arrow : icon;

    const classes = cn(
        BASE,
        SURFACE[kind][resolved] ?? SURFACE.primary[resolved],
        Icon ? dimensions.withIcon : dimensions.bare,
        resolved === 'dark' && 'focus-visible:ring-offset-mk-night',
        className,
    );

    const content = (
        <>
            <span>{children}</span>
            {Icon && (
                <Icon
                    aria-hidden="true"
                    strokeWidth={1.5}
                    className={cn(ICON, Icon === ArrowUpRight && 'motion-safe:fine:group-hover:-translate-y-0.5')}
                />
            )}
        </>
    );

    if (href == null) {
        return (
            <button ref={ref} type={type} className={classes} {...props}>
                {content}
            </button>
        );
    }

    return (
        <SmartLink ref={ref} href={href} external={external} className={classes} {...props}>
            {content}
        </SmartLink>
    );
});

export default Button;
